<script setup lang="ts">
import type { DateValue } from '@internationalized/date';
import { CalendarDateTime, DateFormatter, getLocalTimeZone, parseDate, today } from '@internationalized/date';
import { getDate, getHours, getMinutes, getMonth, getYear } from 'date-fns';
import { CalendarIcon } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import TimeSelect from './TimeSelect.vue';

type Props = {
  modelValue?: string;
  placeholder?: string;
  class?: string;
  granularity?: 'day' | 'hour' | 'minute';
  disablePast?: boolean;
  disableTodayAndPast?: boolean;
  showTodayRelative?: boolean;
  showTomorrowRelative?: boolean;
  showYesterdayRelative?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  placeholder: undefined,
  class: '',
  granularity: 'day',
  disablePast: false,
  disableTodayAndPast: false,
  showTodayRelative: false,
  showTomorrowRelative: false,
  showYesterdayRelative: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const open = ref(false);
const isDateTime = computed(() => props.granularity === 'minute' || props.granularity === 'hour');
const isHourOnly = computed(() => props.granularity === 'hour');

defineExpose({ open: () => (open.value = true) });
const placeholderText = computed(() => {
  if (props.placeholder) return props.placeholder;
  if (props.granularity === 'minute') return 'Pick a date & time';
  if (props.granularity === 'hour') return 'Pick a date & hour';
  return 'Pick a date';
});

const dateOnlyFormatter = new DateFormatter('en-US', { dateStyle: 'long' });
const dateHourFormatter = new DateFormatter('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  hour12: true,
});
const dateTimeFormatter = new DateFormatter('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
const defaultPlaceholder = today(getLocalTimeZone());
const minValue = computed(() => {
  if (props.disableTodayAndPast) return defaultPlaceholder.add({ days: 1 });
  if (props.disablePast) return defaultPlaceholder;
  return undefined;
});

const selectedHour = ref(12);
const selectedMinute = ref(0);
const selectedPeriod = ref<'AM' | 'PM'>('PM');

const to24Hour = (hour12: number, period: 'AM' | 'PM'): number => {
  if (period === 'AM') return hour12 === 12 ? 0 : hour12;
  return hour12 === 12 ? 12 : hour12 + 12;
};

const to12Hour = (hour24: number): { hour12: number; period: 'AM' | 'PM' } => {
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return { hour12, period };
};

const toLocalDateTime = (utcString: string): CalendarDateTime => {
  const d = new Date(utcString);
  return new CalendarDateTime(getYear(d), getMonth(d) + 1, getDate(d), getHours(d), getMinutes(d));
};

const emitDateTime = () => {
  if (!dateValue.value) return;
  const hour24 = to24Hour(selectedHour.value, selectedPeriod.value);
  const dt = new CalendarDateTime(
    dateValue.value.year,
    dateValue.value.month,
    dateValue.value.day,
    hour24,
    isHourOnly.value ? 0 : selectedMinute.value
  );
  emit('update:modelValue', new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute).toISOString());
};

const dateValue = computed<DateValue | undefined>({
  get() {
    if (!props.modelValue) return undefined;
    try {
      return isDateTime.value ? toLocalDateTime(props.modelValue) : parseDate(props.modelValue);
    } catch {
      return undefined;
    }
  },
  set(val: DateValue | undefined) {
    if (!val) return;
    if (isDateTime.value) {
      const hour24 = to24Hour(selectedHour.value, selectedPeriod.value);
      const dt = new CalendarDateTime(
        val.year,
        val.month,
        val.day,
        hour24,
        isHourOnly.value ? 0 : selectedMinute.value
      );
      emit('update:modelValue', new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute).toISOString());
    } else {
      emit('update:modelValue', val.toString());
      open.value = false;
    }
  },
});

watch(
  () => props.modelValue,
  val => {
    if (!val || !isDateTime.value) return;
    const d = new Date(val);
    const { hour12, period } = to12Hour(getHours(d));
    selectedHour.value = hour12;
    if (!isHourOnly.value) selectedMinute.value = getMinutes(d);
    selectedPeriod.value = period;
  },
  { immediate: true }
);

const isSameDay = (a: DateValue, b: DateValue) => a.year === b.year && a.month === b.month && a.day === b.day;

const relativeLabel = (val: DateValue): string | null => {
  if (isDateTime.value) return null;
  const todayValue = today(getLocalTimeZone());
  if (props.showTodayRelative && isSameDay(val, todayValue)) return 'Today';
  if (props.showTomorrowRelative && isSameDay(val, todayValue.add({ days: 1 }))) return 'Tomorrow';
  if (props.showYesterdayRelative && isSameDay(val, todayValue.subtract({ days: 1 }))) return 'Yesterday';
  return null;
};

const displayValue = computed(() => {
  if (!dateValue.value) return placeholderText.value;
  const relative = relativeLabel(dateValue.value);
  if (relative) return relative;
  const formatter = isHourOnly.value ? dateHourFormatter : isDateTime.value ? dateTimeFormatter : dateOnlyFormatter;
  return formatter.format(dateValue.value.toDate(getLocalTimeZone()));
});
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button
        variant="outline"
        :class="cn('w-full justify-start text-left font-normal', !dateValue && 'text-muted-foreground', props.class)"
      >
        <CalendarIcon class="mr-2 h-4 w-4" />
        {{ displayValue }}
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-auto p-0" align="start">
      <div v-if="isDateTime">
        <Calendar v-model="dateValue" :default-placeholder="defaultPlaceholder" :min-value="minValue" initial-focus />
        <TimeSelect
          :hour="selectedHour"
          :minute="selectedMinute"
          :period="selectedPeriod"
          :disable-minute="isHourOnly"
          @update:hour="
            selectedHour = $event;
            emitDateTime();
          "
          @update:minute="
            selectedMinute = $event;
            emitDateTime();
          "
          @update:period="
            selectedPeriod = $event;
            emitDateTime();
          "
        />
      </div>
      <Calendar
        v-else
        v-model="dateValue"
        :default-placeholder="defaultPlaceholder"
        :min-value="minValue"
        initial-focus
      />
    </PopoverContent>
  </Popover>
</template>
